import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EntityConfigType } from '@innovation-os-ng/core/shared';
import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { EntityConfigHelperService } from '@innovation-os-ng/entity-config/core';
import { CompoundFilter, Filter, LeafFilter } from '@itonics/filters';
import { ReplaySubject, catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { SignalsFilters, SignalsType } from '../../shared/model/types';
import { ApiConstants } from '../constants/constants';
import { ClusterDetailModel, ClusterModel, ClusterPostDataModel, clusterDetailPostDataModel } from '../model/model';

@Injectable({
  providedIn: 'root'
})
export class StrongSignalsService {
  /**
   * Store cached cluster details.
   */
  private cachedClusterDetails: { [clusterId: string]: ReplaySubject<ClusterDetailModel> } = {};

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService,
    private readonly entityConfigHelperService: EntityConfigHelperService
  ) {}

  /**
   * Fetch All cluster's list.
   *
   * @param postData - Post data to fetch cluster
   * @returns - Array of fetched clusters.
   */
  public fetchAllCluster(postData: ClusterPostDataModel): Promise<Array<ClusterModel>> {
    const url: string = `${this.environmentService.getRestUrl()}${ApiConstants.FETCH_CLUSTERS}`;
    return firstValueFrom(this.http.post<Array<ClusterModel>>(url, postData).pipe(catchError(() => of([]))));
  }

  /**
   * Fetch All cluster's list.
   * Also, cache the cluster data.
   *
   * @param postData - Post data to fetch cluster details
   * @returns - Array of fetched clusters.
   */
  public fetchClusterDetail(postData: clusterDetailPostDataModel): Promise<ClusterDetailModel> {
    // Get from cache
    let cachedReplaySubject: ReplaySubject<ClusterDetailModel> = this.cachedClusterDetails[postData.clusterId];

    // If not found then create and assign.
    if (!cachedReplaySubject) {
      // Create and assign
      cachedReplaySubject = new ReplaySubject<ClusterDetailModel>(1);
      this.cachedClusterDetails[postData.clusterId] = cachedReplaySubject;

      // Fetch details and notify users.
      const url: string = `${this.environmentService.getRestUrl()}${ApiConstants.FETCH_CLUSTERS_DETAIL}`;
      firstValueFrom(
        this.http.post<ClusterDetailModel>(url, postData).pipe(
          catchError(() => of(null)),
          tap((clusterDetail: ClusterDetailModel) => cachedReplaySubject.next(clusterDetail))
        )
      );
    }

    // Return from cache
    return firstValueFrom(cachedReplaySubject.asObservable());
  }

  /**
   * Fetch the cluster title if available in cache else
   * request and give cluster title.
   *
   * @param clusterId - cluster id.
   * @returns - Returns the id of cluster.
   */
  public async fetchClusterTitle(clusterId: string): Promise<string> {
    const cluster: ReplaySubject<ClusterDetailModel> = this.cachedClusterDetails[clusterId];

    /**
     * If exits in cache then return from there.
     */
    if (cluster) {
      return firstValueFrom(cluster.pipe(map((cluster) => cluster.title)));
    }

    // Fetch the detail
    const clusterDetail: ClusterDetailModel = await this.fetchClusterDetail({ clusterId });
    return Promise.resolve(clusterDetail.title);
  }

  /**
   * Fetch all the entity types
   *
   * @param spaceUri - Current selected space uri
   * @returns - List of entity types
   */
  public fetchElementTypes(spaceUri: string): Promise<Array<EntityConfigType>> {
    return this.entityConfigHelperService.getAllElementTypesBasedOnViewPermission(spaceUri);
  }

  /**
   * Get the applied date filter from the given filter.
   *
   * @param filter - The applied filter.
   */
  public getDateFilter(filter: CompoundFilter): LeafFilter | null {
    const filterClone: CompoundFilter = filter?.clone();

    const selectionFilter: CompoundFilter = this.getSelectionFilter(filterClone);

    // Get the existing date filter.
    const existingDateFilter: LeafFilter = selectionFilter
      .getQueries()
      .find((f) => this.getOnOrIdentifier(f) === SignalsType.PUBLISHED_DATE) as LeafFilter;

    return existingDateFilter ?? null;
  }

  /**
   * Provides the compound filter with selection query.
   *
   * @param filter - Filter to get selection filter from
   * Note: Needed to fetch data in visualization.
   *
   * @returns Compound Filter with selection identifier.
   */
  private getSelectionFilter(filter: CompoundFilter): CompoundFilter {
    // If existing then give that else return new one.
    const selectionFilter: CompoundFilter = filter?.getQuery(SignalsFilters.SELECTION) as CompoundFilter;
    return selectionFilter;
  }

  /**
   * Get On or Identifier based on filter type.
   *
   * @param filter - Filter
   * @returns - Returns the unique On or Id value.
   */
  private getOnOrIdentifier(filter: Filter): string {
    return filter instanceof LeafFilter ? filter.getOn() : filter.getIdentifier();
  }
}
